const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const getTimeValue = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDate = (value) => {
  if (!value) return 'date pending';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'date pending';
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatLabel = (value) => {
  if (!value) return '';
  if (value === 'general') return 'Open Discussion';
  return String(value)
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const clipText = (value, limit = 96) => {
  const text = (value || '').trim();
  if (text.length <= limit) return text;
  return `${text.slice(0, limit - 1).trimEnd()}...`;
};

const getResourceClicks = (resource = {}) => {
  const rawValue = resource?.click_count ?? resource?.clicks ?? 0;
  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isDraftRecording = (recording = {}) => (recording?.status || '').toLowerCase() === 'draft_ready';
const isLiveRecording = (recording = {}) => ['published', 'auto_published'].includes((recording?.status || '').toLowerCase());

const buildSupportAction = (label, tab, roomId = 'support-dads', toolContext = null) => ({
  label,
  tab,
  roomId,
  toolContext,
});

export function buildSupportDadsRoomContext({
  room,
  stats = null,
  recordings = [],
  resources = [],
  resourceInsights = null,
  primaryAction,
  secondaryAction,
}) {
  const postsToday = stats?.posts_today || 0;
  const totalRecordings = stats?.total_recordings || recordings.length || 0;
  const draftRecordings = recordings
    .filter(isDraftRecording)
    .sort((a, b) => getTimeValue(a?.publish_deadline_at || a?.date) - getTimeValue(b?.publish_deadline_at || b?.date));
  const liveRecordings = recordings
    .filter(isLiveRecording)
    .sort((a, b) => getTimeValue(b?.date || b?.created_at) - getTimeValue(a?.date || a?.created_at));
  const rankedResources = [...resources].sort((a, b) => {
    const clickDiff = getResourceClicks(b) - getResourceClicks(a);
    if (clickDiff !== 0) return clickDiff;
    return (a?.name || '').localeCompare(b?.name || '');
  });
  const resourceCount = resources.length;
  const resourceGaps = Array.isArray(resourceInsights?.gaps)
    ? resourceInsights.gaps.filter(Boolean).slice(0, 3)
    : [];
  const topResource = rankedResources[0] || null;
  const supportAssetCount = totalRecordings + resourceCount;

  const movingNowItems = [
    ...liveRecordings.slice(0, 2).map((recording) => ({
      id: recording.recording_id || recording.id || recording.title || `recording-${recording.date || 'unknown'}`,
      eyebrow: (recording?.status || '').toLowerCase() === 'auto_published' ? 'Auto-published' : 'Published',
      title: recording?.title || 'Untitled recording',
      detail: [
        formatLabel(recording?.focus_area),
        recording?.guest_guide_name || 'Guide Partner story',
        recording?.date ? `shared ${formatDate(recording.date)}` : null,
      ].filter(Boolean).join(' · '),
      tone: 'next',
      location: 'In Recordings',
    })),
    ...rankedResources.filter((resource) => getResourceClicks(resource) > 0).slice(0, 2).map((resource) => ({
      id: resource.resource_id || resource.id || resource.name || `resource-${resource.category || 'unknown'}`,
      eyebrow: 'Resource landing',
      title: resource?.name || 'Untitled resource',
      detail: [
        `${pluralize(getResourceClicks(resource), 'click')}`,
        formatLabel(resource?.category),
        clipText(resource?.recommendation_note, 72),
      ].filter(Boolean).join(' · '),
      tone: getResourceClicks(resource) > 10 ? 'next' : 'steady',
      location: 'In Resources',
    })),
  ].slice(0, 4);

  const needsYourMoveItems = [
    ...draftRecordings.slice(0, 3).map((recording) => ({
      id: recording.recording_id || recording.id || recording.title || `draft-${recording.date || 'unknown'}`,
      eyebrow: 'Review next',
      title: recording?.title || 'Untitled focus draft',
      detail: [
        formatLabel(recording?.focus_area),
        recording?.publish_deadline_at ? `deadline ${formatDate(recording.publish_deadline_at)}` : null,
        recording?.guest_guide_name || 'Guide Partner story',
      ].filter(Boolean).join(' · '),
      tone: 'attention',
      location: 'In Recordings',
      action: buildSupportAction('Open Recordings', 'recordings'),
    })),
    ...resourceGaps.slice(0, 2).map((gap, index) => ({
      id: `support-gap-${index}`,
      eyebrow: 'Gap to close',
      title: gap,
      detail: topResource
        ? `Resource analysis still flags this gap even though ${topResource.name} is already getting attention.`
        : 'Resource analysis still flags this gap in the support library.',
      tone: 'attention',
      location: 'In Resources',
      action: buildSupportAction('Open Resources', 'resources'),
    })),
  ].slice(0, 4);

  const suggestedActions = [];

  if (draftRecordings.length > 0) {
    suggestedActions.push({
      tone: 'attention',
      eyebrow: 'Do first',
      title: `Review ${pluralize(draftRecordings.length, 'focus draft')}`,
      detail: 'Decide which story or takeaway should go live next so dads get help while the signal is still fresh.',
      location: 'In Recordings',
      action: buildSupportAction('Open Recordings', 'recordings'),
    });
  }

  if (resourceGaps.length > 0) {
    suggestedActions.push({
      tone: 'next',
      eyebrow: draftRecordings.length > 0 ? 'Then' : 'Do first',
      title: 'Fill the next support gap',
      detail: `Start with ${resourceGaps[0]} so the library stays useful for the dads who need it most.`,
      location: 'In Resources',
      action: buildSupportAction('Open Resources', 'resources'),
    });
  }

  if (postsToday > 0) {
    suggestedActions.push({
      tone: 'next',
      eyebrow: suggestedActions.length > 0 ? 'Keep close' : 'Do first',
      title: 'Let today\'s dad signals shape the next care move',
      detail: 'Use the latest posts and feedback to decide whether a recording, prompt, or resource should move next.',
      location: 'In Dad Partners',
      action: buildSupportAction('Open Feedback', 'feedback', 'listen-learn'),
    });
  } else if (topResource) {
    suggestedActions.push({
      tone: 'steady',
      eyebrow: suggestedActions.length > 0 ? 'Keep warm' : 'Do first',
      title: `Refresh support around ${topResource.name}`,
      detail: 'When something is already landing with dads, build companion support around it instead of starting from scratch.',
      location: 'In Resources',
      action: buildSupportAction('Open Resources', 'resources'),
    });
  }

  if (!suggestedActions.length) {
    suggestedActions.push({
      tone: 'steady',
      eyebrow: 'Quiet',
      title: 'Keep the support library ready for the next real need',
      detail: 'Nothing is pressing right now, so this is a good moment to keep recordings and resources orderly and useful.',
      location: 'In Dad Partners',
      action: buildSupportAction('Open Recordings', 'recordings'),
    });
  }

  const statusTone = draftRecordings.length > 0
    ? 'attention'
    : resourceGaps.length > 0
      ? 'next'
      : postsToday > 0 || movingNowItems.length > 0
        ? 'next'
        : 'steady';

  const statusLabel = draftRecordings.length > 0
    ? 'Needs review'
    : resourceGaps.length > 0
      ? 'Strengthen support'
      : postsToday > 0 || movingNowItems.length > 0
        ? 'In motion'
        : 'Steady';

  return {
    id: room.id,
    variant: 'support-dads',
    statusTone,
    statusLabel,
    startTitle: draftRecordings.length > 0
      ? `Review ${pluralize(draftRecordings.length, 'focus draft')} and decide what should serve dads next`
      : resourceGaps.length > 0
        ? 'Strengthen the support library where dads may still be underserved'
        : postsToday > 0
          ? 'Match today\'s dad signals with the support that should move next'
          : 'Keep the support library warm, practical, and ready when dads need it',
    startDetail: 'This room should keep care practical by turning what dads are carrying into recordings, prompts, peer support, and resources that are ready to help.',
    signals: [
      {
        tone: postsToday > 0 ? 'next' : 'steady',
        text: postsToday > 0
          ? `${pluralize(postsToday, 'dad post')} came in today and can guide the next support move`
          : 'No fresh dad-post surge is shaping support work right now.',
      },
      {
        tone: draftRecordings.length > 0 ? 'attention' : 'steady',
        text: draftRecordings.length > 0
          ? `${pluralize(draftRecordings.length, 'recording draft')} are waiting on review before they can help dads`
          : 'No recording drafts are waiting right now.',
      },
      {
        tone: resourceGaps.length > 0 ? 'attention' : topResource ? 'next' : 'steady',
        text: resourceGaps.length > 0
          ? `${pluralize(resourceGaps.length, 'resource gap')} still need stronger coverage`
          : topResource
            ? `${topResource.name} is already getting traction in the resource library`
            : `${pluralize(resourceCount, 'resource')} currently in the library`,
      },
    ],
    metrics: [
      { label: 'Dad posts today', value: postsToday, detail: 'Live care signals from the community', tone: postsToday > 0 ? 'next' : 'steady' },
      {
        label: 'Support assets',
        value: supportAssetCount,
        detail: `${pluralize(totalRecordings, 'recording')} and ${pluralize(resourceCount, 'resource')} ready to serve`,
        tone: supportAssetCount > 0 ? 'next' : 'steady',
      },
      { label: 'Drafts ready', value: draftRecordings.length, detail: 'Focus reviews waiting on Kevin', tone: draftRecordings.length > 0 ? 'attention' : 'steady' },
    ],
    primaryAction,
    secondaryAction,
    movingNowItems,
    needsYourMoveItems,
    suggestedActions: suggestedActions.slice(0, 3),
  };
}
